<template>
  <div class="tag-management">
    <h2>标签管理</h2>
    
    <!-- 添加标签表单 -->
    <div class="add-tag-form">
      <h3>添加新标签</h3>
      <form @submit.prevent="addTag">
        <div class="form-group">
          <label for="tagName">标签名称:</label>
          <input type="text" id="tagName" v-model="newTag.name" required maxlength="50" />
        </div>
        
        <div class="form-group">
          <label for="tagDescription">标签描述:</label>
          <textarea id="tagDescription" v-model="newTag.description" rows="3"></textarea>
        </div>
        
        <button type="submit" :disabled="addingTag">
          {{ addingTag ? '添加中...' : '添加标签' }}
        </button>
      </form>
    </div>
    
    <!-- 标签列表 -->
    <div class="tag-list">
      <h3>标签列表</h3>
      <div v-if="loading">加载中...</div>
      <div v-else-if="tags.length === 0">暂无标签</div>
      <div v-else>
        <table class="tag-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>标签名称</th>
              <th>描述</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tag in tags" :key="tag.id">
              <td>{{ tag.id }}</td>
              <td>{{ tag.name }}</td>
              <td>{{ tag.description || '—' }}</td>
              <td>{{ formatDateTime(tag.createdAt) }}</td>
              <td>
                <div class="action-buttons">
                  <button 
                    class="btn-edit" 
                    @click="showEditTagModal(tag)"
                    :disabled="editingTag"
                  >
                    编辑
                  </button>
                  <button 
                    class="btn-delete" 
                    @click="confirmDeleteTag(tag)"
                    :disabled="deletingTag"
                  >
                    删除
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 编辑标签模态框 -->
    <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
      <div class="modal-content" @click.stop>
        <h3>编辑标签</h3>
        <p>标签ID: {{ selectedTag.id }}</p>
        <form @submit.prevent="updateTag">
          <div class="form-group">
            <label for="editTagName">标签名称:</label>
            <input 
              type="text" 
              id="editTagName" 
              v-model="editForm.name" 
              required
              maxlength="50"
            >
          </div>
          <div class="form-group">
            <label for="editTagDescription">标签描述:</label>
            <textarea 
              id="editTagDescription" 
              v-model="editForm.description" 
              rows="3"
            ></textarea>
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeEditModal" :disabled="editingTag">
              取消
            </button>
            <button type="submit" :disabled="editingTag">
              {{ editingTag ? '保存中...' : '确认保存' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { tagService } from '@/services/tagService'

export default {
  name: 'TagManagement',
  data() {
    return {
      tags: [],
      loading: true,
      addingTag: false,
      deletingTag: false,
      editingTag: false,
      showEditModal: false,
      selectedTag: null,
      newTag: {
        name: '',
        description: ''
      },
      editForm: {
        name: '',
        description: ''
      }
    }
  },
  mounted() {
    // 检查用户角色
    const { authService } = require('@/services/authService');
    if (!authService.isAdmin() && !authService.isCourseAdmin()) {
      this.$router.push('/dashboard/profile');
      return;
    }
    
    this.loadTags()
  },
  methods: {
    loadTags() {
      this.loading = true
      tagService.getAllTags()
        .then(response => {
          if (response.success) {
            this.tags = response.data || []
          } else {
            console.error('获取标签列表失败:', response.message)
          }
          this.loading = false
        })
        .catch(error => {
          console.error('获取标签列表失败:', error)
          this.loading = false
          
          // 如果是权限错误，跳转到统计数据页面
          if (error.response && error.response.status === 403) {
            this.$router.push('/dashboard/profile')
          }
        })
    },
    
    addTag() {
      this.addingTag = true
      tagService.createTag(this.newTag)
        .then(response => {
          if (response.success) {
            alert('标签添加成功')
            this.newTag = { name: '', description: '' }
            this.loadTags()
          } else {
            alert('添加标签失败: ' + (response.message || response.error))
          }
          this.addingTag = false
        })
        .catch(error => {
          this.addingTag = false
          console.error('添加标签失败:', error)
          const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || '添加标签失败'
          alert(errorMessage)
        })
    },

    showEditTagModal(tag) {
      this.selectedTag = tag
      this.editForm = {
        name: tag.name,
        description: tag.description || ''
      }
      this.showEditModal = true
    },

    closeEditModal() {
      this.showEditModal = false
      this.selectedTag = null
      this.editForm = {
        name: '',
        description: ''
      }
    },

    updateTag() {
      this.editingTag = true
      tagService.updateTag(this.selectedTag.id, this.editForm)
        .then(response => {
          if (response.success) {
            alert('标签更新成功')
            this.closeEditModal()
            this.loadTags()
          } else {
            alert('更新标签失败: ' + (response.message || response.error))
          }
          this.editingTag = false
        })
        .catch(error => {
          this.editingTag = false
          console.error('更新标签失败:', error)
          const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || '更新标签失败'
          alert(errorMessage)
        })
    },

    confirmDeleteTag(tag) {
      if (confirm(`确定要删除标签 "${tag.name}" 吗？如果标签已被课程使用，删除将失败。`)) {
        this.deleteTag(tag)
      }
    },

    deleteTag(tag) {
      this.deletingTag = true
      tagService.deleteTag(tag.id)
        .then(response => {
          if (response.success) {
            alert('标签删除成功')
            this.loadTags()
          } else {
            alert('删除标签失败: ' + (response.message || response.error))
          }
          this.deletingTag = false
        })
        .catch(error => {
          this.deletingTag = false
          console.error('删除标签失败:', error)
          const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || '删除标签失败'
          alert(errorMessage)
        })
    },

    formatDateTime(value) {
      if (!value) return '—'
      // 如果已经是格式化好的字符串，直接返回
      if (typeof value === 'string' && value.includes(' ')) {
        return value
      }
      // 如果是ISO格式，转换为本地时间
      if (typeof value === 'string' && value.includes('T')) {
        const date = new Date(value)
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }).replace(/\//g, '-')
      }
      return value
    }
  }
}
</script>

<style scoped>
.tag-management {
  padding: 2rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.add-tag-form {
  background: linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%);
  padding: 2rem;
  border-radius: 20px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
}

.add-tag-form h3 {
  margin: 0 0 1.5rem 0;
  color: #1e293b;
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #1e293b, #475569);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  box-sizing: border-box;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(255,255,255,0.8);
  font-family: inherit;
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  background: rgba(255,255,255,0.95);
}

button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

button:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

button:disabled {
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.tag-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
}

.tag-table th,
.tag-table td {
  padding: 1.25rem 1.5rem;
  text-align: left;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.tag-table th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tag-table tr:hover {
  background: linear-gradient(90deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  transform: scale(1.01);
  transition: all 0.3s ease;
}

.tag-table tr:last-child td {
  border-bottom: none;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.btn-edit {
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(23, 162, 184, 0.3);
}

.btn-edit:hover {
  background: linear-gradient(135deg, #138496 0%, #0f6674 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(23, 162, 184, 0.4);
}

.btn-delete {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.btn-delete:hover {
  background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.4);
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 400px;
  max-width: 500px;
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: 10px;
  color: #333;
}

.modal-content p {
  margin-bottom: 20px;
  color: #666;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.modal-actions button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.modal-actions button[type="button"] {
  background-color: #6c757d;
  color: white;
}

.modal-actions button[type="button"]:hover {
  background-color: #5a6268;
}

.modal-actions button[type="submit"] {
  background-color: #007bff;
  color: white;
}

.modal-actions button[type="submit"]:hover {
  background-color: #0056b3;
}

.modal-actions button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}
</style>

